import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { appLinks } from '../../lib/crossSiteLinks';
import { tokens } from '../../components/SEOPageLayout';
import { BreadcrumbJsonLd, FaqJsonLd } from '../../components/StructuredData';
import { SeoLongformSections } from '../../components/SeoLongformSections';

export default function AiAssistedDevelopmentEvidencePage() {
  const faqs = [
    {
      question: 'Does EvidentTrail read prompts or generated code?',
      answer: 'No. We only capture metadata signals, not prompts or source code content.'
    },
    {
      question: 'How is AI usage detected?',
      answer: 'We use explicit GitHub metadata signals like co-author trailers and labels.'
    },
    {
      question: 'Does this certify AI compliance?',
      answer: 'No. It provides evidence that supports audit review, not certification.'
    },
    {
      question: 'Can I run a one-off report?',
      answer: 'Yes. Guest mode supports a single evidence pack with short retention.'
    }
  ];

  const longformSections = [
    {
      title: 'What auditors ask for',
      paragraphs: [
        'AI-assisted development reviews focus on evidence of human oversight, approvals before merge, and traceable change records.'
      ],
      bullets: [
        'Human approval after AI signals are detected',
        'Reviewer identity and timestamps',
        'Reproducible evidence packs for sampling'
      ]
    },
    {
      title: 'Evidence artifacts included',
      paragraphs: [
        'Evidence packs link AI metadata signals to approvals and required checks without storing source code.'
      ],
      bullets: [
        'AI signal metadata (co-author trailers, labels)',
        'PR approvals and reviewer identities',
        'Required check status metadata'
      ]
    },
    {
      title: 'How EvidentTrail works (read-only)',
      paragraphs: [
        'EvidentTrail captures explicit metadata signals from GitHub and associates them with approval evidence. No prompts or code are stored.'
      ],
      bullets: [
        'Read-only GitHub App access',
        'Metadata-only evidence capture',
        'Exportable packs with checksums'
      ]
    },
    {
      title: 'How to interpret the output',
      paragraphs: [
        'Use the AI signal indicators to show where AI assistance occurred and confirm approvals occurred after the signal.'
      ],
      bullets: [
        'AI signals listed alongside approvals',
        'Approval timestamps confirm human review',
        'Checksums validate export integrity'
      ]
    },
    {
      title: 'Common failure modes',
      paragraphs: [
        'AI-assisted changes without a linked human approval or a missing metadata trail often create audit findings.'
      ],
      bullets: [
        'AI signals without human approval evidence',
        'No record of AI metadata for sampled changes',
        'Screenshots instead of metadata exports'
      ]
    }
  ];

  return (
    <MarketingLayout
      title="AI-Assisted Development Audit Evidence | EvidentTrail"
      description="Prove human oversight of AI-generated code. EvidentTrail detects AI commits and links them to human peer reviews for SOC 2 and ISO compliance."
      h1="Auditing AI-Assisted Development"
      heroText="Embrace AI tools like Copilot while maintaining the human oversight evidence auditors require."
      type="article"
    >
      <BreadcrumbJsonLd
        items={[
          { name: 'Home', url: 'https://evidenttrail.com/' },
          { name: 'AI-Assisted Development', url: 'https://evidenttrail.com/ai-assisted-development-evidence' }
        ]}
      />
      <FaqJsonLd items={faqs} />
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>The "Human Oversight" Requirement</h2>
            <p className={`${tokens.text.base} mb-6`}>
              New regulations (like the EU AI Act) and updated standards (ISO 42001) often require proof of "Human in the Loop" for AI-generated assets.
              In software development, this means proving a human reviewed Copilot suggestions.
            </p>

            <div className={tokens.card}>
              <h3 className={`${tokens.heading.h3} mb-4`}>How EvidentTrail Solves This</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary-600/20 rounded-lg flex items-center justify-center flex-shrink-0 text-2xl">🤖</div>
                  <div>
                    <strong className="text-white block mb-1">Detection</strong>
                    <p className="text-sm text-slate-400">We analyze commit metadata to detect signatures of AI assistants (e.g., specific commit messages or co-author tags).</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary-600/20 rounded-lg flex items-center justify-center flex-shrink-0 text-2xl">👀</div>
                  <div>
                    <strong className="text-white block mb-1">Linkage</strong>
                    <p className="text-sm text-slate-400">We explicitly link these commits to the Pull Request Review event, identifying the human reviewer.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary-600/20 rounded-lg flex items-center justify-center flex-shrink-0 text-2xl">📜</div>
                  <div>
                    <strong className="text-white block mb-1">Evidence</strong>
                    <p className="text-sm text-slate-400">The final audit log shows: "AI-Assisted Change -&gt; Reviewed by Jane Doe -&gt; Approved -&gt; Merged".</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="mt-12 text-center">
              <a href={appLinks.register} className="btn btn-primary px-8 py-3">
                Start Tracking AI Oversight
              </a>
            </div>
          </div>
        </div>
      </section>

      <SeoLongformSections sections={longformSections} faqs={faqs} />

      <section className={tokens.section}>
        <div className={tokens.container}>
          <h2 className={`${tokens.heading.h3} mb-8 text-center`}>Related AI Topics</h2>
          <div className="grid md:grid-cols-5 gap-6">
            <Link to="/eu-ai-act-github-compliance" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">EU AI Act</h4>
              <p className="text-sm text-slate-400">Implications of the EU AI Act for software delivery teams.</p>
            </Link>
            <Link to="/github-change-control" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">Change Control</h4>
              <p className="text-sm text-slate-400">Standard change control principles applied to AI workflows.</p>
            </Link>
            <Link to="/features/exception-tracking" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">Exceptions</h4>
              <p className="text-sm text-slate-400">Handling bypasses when AI code breaks specific policies.</p>
            </Link>
            <Link to="/tools/pr-approvals-report" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">PR Approvals Exporter</h4>
              <p className="text-sm text-slate-400">Generate an evidence pack showing human approvals.</p>
            </Link>
            <Link to="/security" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">Security</h4>
              <p className="text-sm text-slate-400">Read-only access and data minimization boundaries.</p>
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
